import { useMutation } from '@tanstack/react-query';
import { DOCUMENTS_API } from '@Constants/backendConstants';
import { IIdWithDisplay, IWithStatus, isOk, Result, useToken } from '@synqo/frontend-component-library';
import { useCallback, useRef } from 'react';

interface IUploadStaticFile {
    uploadFile: (file: File) => Promise<Result<ApiStaticFile>>;
    queueFile: (fileInfo: IPendingFile) => void;
    processQueueItem: () => Promise<UploadPendingFileResult | undefined>;
    reset: () => void;
}

interface ApiStaticFile extends IIdWithDisplay, IWithStatus {
    file_name: string;
    mime_type: string;
}

interface UploadPendingFileResult {
    localId: string;
    file: ApiStaticFile;
}

interface IPendingFileUpload {
    operation: 'upload';
    file: File;
    fileId: string;
}

interface IPendingFileDelete {
    operation: 'delete';
    fileId: string;
}

type IPendingFile = IPendingFileUpload | IPendingFileDelete;

export const useUploadStaticFile = (): IUploadStaticFile => {
    const fileQueueRef = useRef<IPendingFile[]>([]);

    const { getToken } = useToken();

    const { mutateAsync: uploadFile } = useMutation({
        mutationFn: async (file: File): Promise<Result<ApiStaticFile>> => {
            const token = await getToken();

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${DOCUMENTS_API}/files`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                credentials: 'include',
                method: 'post',
                body: formData,
            });

            return await response.json();
        },
    });

    const queueFile = useCallback(
        (fileInfo: IPendingFile) => {
            if (fileInfo.operation === 'delete') {
                fileQueueRef.current = fileQueueRef.current.filter((f) => f.fileId !== fileInfo.fileId);
            }

            fileQueueRef.current.push(fileInfo);
        },
        [fileQueueRef],
    );

    const processQueueItem = useCallback(async () => {
        const pendingFile = fileQueueRef.current.pop();
        if (pendingFile === undefined) {
            return undefined;
        }

        if (pendingFile.operation === 'upload') {
            const result = await uploadFile(pendingFile.file);
            if (isOk(result)) {
                return {
                    localId: pendingFile.fileId,
                    file: result.data,
                } satisfies UploadPendingFileResult;
            }
        }

        return undefined;
    }, [fileQueueRef, uploadFile]);

    const reset = useCallback(() => {
        fileQueueRef.current = [];
    }, [fileQueueRef]);

    return {
        uploadFile,
        queueFile,
        processQueueItem,
        reset,
    };
};
